/**
 * BlogErrorPage - Friendly error page for blog-related errors
 *
 * Shows a child-friendly message when blog posts fail to load,
 * with options to go back or visit the main blog page.
 */

import { Link, useRouteError, isRouteErrorResponse } from "react-router-dom";
import { Button } from "../../components";

export function BlogErrorPage() {
  const error = useRouteError();

  // Get error message for logging (not displayed to users)
  let errorMessage = "Unknown error";
  if (isRouteErrorResponse(error)) {
    errorMessage = error.statusText;
  } else if (error instanceof Error) {
    errorMessage = error.message;
  }

  // Log error for debugging
  console.error("Blog error:", errorMessage);

  return (
    <div className="min-h-screen bg-gradient-to-b from-magic-50 to-white">
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        {/* Friendly illustration */}
        <div className="mb-8">
          <svg
            width="120"
            height="120"
            viewBox="0 0 120 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mx-auto"
            aria-hidden="true"
          >
            {/* Book with sparkles */}
            <rect
              x="20"
              y="35"
              width="80"
              height="60"
              rx="4"
              fill="#E8DEF8"
              stroke="#7C3AED"
              strokeWidth="3"
            />
            <rect x="25" y="40" width="70" height="50" rx="2" fill="white" />
            <line
              x1="60"
              y1="40"
              x2="60"
              y2="90"
              stroke="#E8DEF8"
              strokeWidth="2"
            />
            {/* Question marks */}
            <text
              x="40"
              y="72"
              fontSize="24"
              fill="#7C3AED"
              fontWeight="bold"
            >
              ?
            </text>
            <text
              x="72"
              y="72"
              fontSize="24"
              fill="#7C3AED"
              fontWeight="bold"
            >
              ?
            </text>
            {/* Sparkles */}
            <circle cx="15" cy="30" r="4" fill="#FFD93D" />
            <circle cx="105" cy="25" r="3" fill="#FFD93D" />
            <circle cx="100" cy="85" r="5" fill="#FFD93D" />
          </svg>
        </div>

        <h1 className="font-display font-bold text-display-md text-neutral-900 mb-4">
          Oops! Something went wrong
        </h1>

        <p className="font-body text-body-lg text-neutral-600 mb-8 max-w-md mx-auto">
          We couldn't load this page right now. Don't worry – our team has been
          notified and we're working on it!
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/blog">
            <Button variant="primary" size="lg">
              Back to Blog
            </Button>
          </Link>
          <Link to="/">
            <Button variant="secondary" size="lg">
              Go Home
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default BlogErrorPage;
